import { useCallback, useMemo } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import CostEstimateSideMsg from '#/modules/crmMgmt/costEstimate/components/CostEstimateSideMsg';
import { buildCostEstimateColumns } from '#/modules/crmMgmt/costEstimate/data/costEstimateColumns';
import { getCostEstimateApiErrorMessage } from '#/modules/crmMgmt/costEstimate/helper/apiError';
import useCostEstimateList from '#/modules/crmMgmt/costEstimate/hooks/useCostEstimateList';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';

const COST_ESTIMATE_SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  name: '試算名稱',
  updatedAt: '更新時間',
};

const CostEstimateListPage = () => {
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.COST_ESTIMATE_CREATE);
  const canUpdate = useCan(PERM.COST_ESTIMATE_UPDATE);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setSort,
    removeSortAt,
    resetListQuery,
  } = useCostEstimateList();

  const columns = useMemo(
    () => buildCostEstimateColumns({ canShowEdit: () => canUpdate }),
    [canUpdate],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    setSelectedData(isSelected ? row.original : null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.costEstimateCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <h1 className="page_title">
            <span className="border-l-5 border-primary-400 pl-2.5">成本試算列表</span>
          </h1>
        </div>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={
                isError
                  ? getCostEstimateApiErrorMessage(error, '載入列表失敗')
                  : undefined
              }
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋試算名稱"
              listQuery={listQuery}
              filterStatusFields={[]}
              sortFieldLabels={COST_ESTIMATE_SORT_FIELD_LABELS}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
              headerAction={headerAction}
            />
          </div>

          <CostEstimateSideMsg displayData={displayData} />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default CostEstimateListPage;
